import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Alert, Button, Switch, message, Spin } from 'antd';
import { ArrowLeftOutlined, EditOutlined, PlusOutlined, ReloadOutlined, TeamOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { getAccessibleTrip, updateTrip } from '../../services/tripsService';
import { loadWeatherForCityDay } from '../../services/weatherOrchestrator';
import {
  loadLocalUserPreferences,
  patchAppUserSettings,
} from '../../services/userPreferencesSyncService';
import type { Trip } from '../../types/client';
import type { ForecastDay } from '../../types/weather';
import { tripDetailEn } from '../../content/trips.en';
import { weatherEn } from '../../content/weather.en';
import { tripShareEn } from '../../content/tripShare.en';
import { resolveTripTemperatureUnit } from '../../utils/temperatureUnit';
import { mapWithConcurrency } from '../../utils/asyncPool';
import CruiseCountdownCard from '../../components/CruiseCountdownCard/CruiseCountdownCard';
import TripChecklistDrawer from '../../components/TripChecklistDrawer/TripChecklistDrawer';
import NotesAttachmentsCard from '../../components/NotesAttachmentsCard/NotesAttachmentsCard';
import CityVisitCard from '../../components/CityVisitCard/CityVisitCard';
import TripShareRoster from '../../components/TripShareRoster/TripShareRoster';
import { AccommodationIcon } from '../../components/icons/TripIcons';
import {
  buildTripDayCards,
  defaultShowPlanIcons,
  formatTripHeaderDate,
} from '../../utils/tripDayCards';
import { isPastVisitDate } from '../../utils/preferenceFilters';
import { isAtSeaLocation } from '../../services/cruiseItinerariesApi';
import {
  canOpenAttachments,
  canShowShareUI,
  effectiveCanEdit,
} from '../../utils/tripAccessPolicy';
import styles from './TripDetail.module.css';

/** Cap parallel Open-Meteo loads on long cruises (e.g. 175 ports). */
const TRIP_DETAIL_WEATHER_CONCURRENCY = 4;

const TripDetail: React.FC = () => {
  const { tripId } = useParams<{ tripId: string }>();
  const navigate = useNavigate();
  const { user, isDataSyncing } = useAuth();
  const [trip, setTrip] = useState<Trip | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPlanIcons, setShowPlanIcons] = useState(() => {
    const prefs = loadLocalUserPreferences().appUserSettings;
    return prefs.showPlanIcons ?? true;
  });
  const [hidePastCities, setHidePastCities] = useState(
    () => loadLocalUserPreferences().appUserSettings.hidePastCities
  );
  const [hidePastDays, setHidePastDays] = useState(
    () => loadLocalUserPreferences().appUserSettings.hidePastDays
  );
  const [weatherByKey, setWeatherByKey] = useState<Record<string, ForecastDay | null>>({});
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const reload = useCallback((): void => {
    if (!user?.sub || !tripId) {
      setTrip(null);
      setLoading(false);
      return;
    }
    const next = getAccessibleTrip(user.sub, tripId);
    setTrip(next);
    const prefs = loadLocalUserPreferences().appUserSettings;
    setHidePastCities(prefs.hidePastCities);
    setHidePastDays(prefs.hidePastDays);
    if (next) {
      setShowPlanIcons(
        prefs.showPlanIcons != null ? prefs.showPlanIcons : defaultShowPlanIcons(next)
      );
    }
    setLoading(false);
  }, [user?.sub, tripId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!isDataSyncing) reload();
  }, [isDataSyncing, reload]);

  const handleShowPlanIconsChange = (checked: boolean): void => {
    setShowPlanIcons(checked);
    patchAppUserSettings({ showPlanIcons: checked });
  };

  const persist = (updates: Partial<Omit<Trip, 'id'>>): void => {
    if (!user?.sub || !tripId) return;
    const updated = updateTrip(user.sub, tripId, updates);
    if (updated) setTrip(updated);
  };

  const handleToggleTask = (taskId: string): void => {
    if (!trip) return;
    const taskItems = (trip.taskItems ?? []).map((t) =>
      t.id === taskId ? { ...t, isDone: !t.isDone } : t
    );
    persist({ taskItems });
  };

  const handleDeleteTask = (taskId: string): void => {
    if (!trip) return;
    persist({ taskItems: (trip.taskItems ?? []).filter((t) => t.id !== taskId) });
  };

  const handleAddTask = (title: string): void => {
    if (!trip) return;
    const existing = trip.taskItems ?? [];
    const nextOrder =
      existing.reduce((max, t) => Math.max(max, t.sortOrder), -1) + 1;
    persist({
      taskItems: [
        ...existing,
        {
          id: crypto.randomUUID(),
          title,
          isDone: false,
          sortOrder: nextOrder,
        },
      ],
    });
  };

  const dayCards = useMemo(() => (trip ? buildTripDayCards(trip) : []), [trip]);
  const visibleDayCards = useMemo(() => {
    if (!hidePastCities && !hidePastDays) return dayCards;
    return dayCards.filter(
      (c) => !isPastVisitDate(c.departureDate, c.arrivalDate)
    );
  }, [dayCards, hidePastCities, hidePastDays]);
  const temperatureUnit = trip ? resolveTripTemperatureUnit(trip) : 'F';

  const loadWeatherForCards = useCallback(
    async (force = false): Promise<void> => {
      if (!user?.sub || !trip) return;

      const cardsToLoad = visibleDayCards.filter(
        (c) => !c.isAtSea && c.arrivalDate && c.cityId
      );
      if (cardsToLoad.length === 0) return;

      setWeatherLoading(true);
      try {
        await mapWithConcurrency(cardsToLoad, TRIP_DETAIL_WEATHER_CONCURRENCY, async (card) => {
          const key = `${card.cityId}_${card.arrivalDate}`;
          const coords = trip.cityCoords?.[card.cityId];
          const result = await loadWeatherForCityDay({
            userId: user.sub,
            cityVisitId: card.cityId,
            lat: coords?.latitude,
            lng: coords?.longitude,
            date: card.arrivalDate!,
            force,
          });
          setWeatherByKey((prev) => ({ ...prev, [key]: result.day }));
        });
      } finally {
        setWeatherLoading(false);
      }
    },
    [user?.sub, trip, visibleDayCards]
  );

  useEffect(() => {
    void loadWeatherForCards();
  }, [loadWeatherForCards]);
  const attachmentCityOptions = useMemo(
    () =>
      dayCards
        .filter((c) => !isAtSeaLocation(c.cityName))
        .reduce<{ id: string; name: string }[]>((acc, c) => {
          if (acc.some((x) => x.id === c.cityId)) return acc;
          acc.push({ id: c.cityId, name: c.cityName });
          return acc;
        }, []),
    [dayCards]
  );
  const canPlan = Boolean(trip?.startDate && trip?.endDate);
  const sub = user?.sub ?? '';
  const canEditTrip = trip ? effectiveCanEdit(trip, sub) : false;
  const showShare = trip ? canShowShareUI(trip, sub) : false;
  const attachmentsOk = trip ? canOpenAttachments(trip, sub) : true;
  const title = trip?.title?.trim() || tripDetailEn.untitled;
  const headerStart = formatTripHeaderDate(trip?.startDate ?? null);
  const headerEnd = formatTripHeaderDate(trip?.endDate ?? null);
  const headerDate =
    headerStart && headerEnd && headerStart !== headerEnd
      ? `${headerStart} — ${headerEnd}`
      : headerStart ?? headerEnd;

  if (loading || isDataSyncing) {
    return (
      <div className={styles.tripDetail}>
        <div className={styles.container}>
          <div className={styles.loading}>
            <Spin size="large" />
          </div>
        </div>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className={styles.tripDetail}>
        <div className={styles.container}>
          <p>Trip not found.</p>
          <Button type="primary" onClick={() => navigate('/trips')}>
            {tripDetailEn.back}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.tripDetail}>
      <div className={styles.container}>
        <div className={styles.topBar}>
          <Link to="/trips" className={styles.backLink}>
            <ArrowLeftOutlined /> {tripDetailEn.back}
          </Link>
          {showShare ? (
            <Button icon={<TeamOutlined />} onClick={() => setShareOpen(true)}>
              {tripShareEn.shareTrip}
            </Button>
          ) : null}
        </div>

        <div className={styles.stack}>
          {!canEditTrip ? (
            <Alert type="info" showIcon message={tripShareEn.readOnlyBanner} />
          ) : null}
          {!attachmentsOk ? (
            <Alert type="warning" showIcon message={tripShareEn.attachmentOwnerOnly} />
          ) : null}
          <TripChecklistDrawer
            trip={trip}
            onToggleTask={canEditTrip ? handleToggleTask : () => undefined}
            onDeleteTask={canEditTrip ? handleDeleteTask : () => undefined}
            onAddTask={canEditTrip ? handleAddTask : () => undefined}
          />

          {trip.startDate ? (
            <CruiseCountdownCard
              tripName={title}
              tripType={trip.tripType}
              startDate={trip.startDate}
              showsHeader={false}
            />
          ) : null}

          <h1 className={styles.tripTitle}>{tripDetailEn.tripHeader(title, headerDate)}</h1>

          {visibleDayCards.length > 0 ? (
            <div className={styles.weatherRefreshRow}>
              <Button
                type="link"
                size="small"
                icon={<ReloadOutlined />}
                loading={weatherLoading}
                onClick={() => void loadWeatherForCards(true)}
              >
                {weatherEn.refresh}
              </Button>
            </div>
          ) : null}

          {dayCards.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyTitle}>{tripDetailEn.noDays}</div>
              <p className={styles.emptyDescription}>{tripDetailEn.noDaysDescription}</p>
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => navigate(`/trips/${trip.id}/edit`)}
              >
                {tripDetailEn.addCity}
              </Button>
            </div>
          ) : visibleDayCards.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyTitle}>{tripDetailEn.noDays}</div>
            </div>
          ) : (
            <div className={styles.dayList}>
              {visibleDayCards.map((card) => {
                const weatherKey = card.arrivalDate
                  ? `${card.cityId}_${card.arrivalDate}`
                  : null;
                return (
                  <CityVisitCard
                    key={`${card.cityId}_${card.arrivalDate ?? ''}`}
                    tripId={trip.id}
                    card={card}
                    trip={trip}
                    showPlanIcons={showPlanIcons}
                    canPlan={canPlan}
                    weather={weatherKey ? weatherByKey[weatherKey] ?? null : null}
                    temperatureUnit={temperatureUnit}
                    weatherLoading={weatherLoading && weatherKey != null && !(weatherKey in weatherByKey)}
                  />
                );
              })}
            </div>
          )}

          {dayCards.length > 0 ? (
            <NotesAttachmentsCard
              trip={trip}
              cityOptions={attachmentCityOptions}
              defaultExpanded
              onChangeTrip={canEditTrip ? persist : () => undefined}
            />
          ) : null}
        </div>
      </div>

      <TripShareRoster
        open={shareOpen}
        tripId={trip.id}
        onClose={() => setShareOpen(false)}
      />

      <div className={styles.footer}>
        <div className={styles.footerActions}>
          <Button
            type="primary"
            className={styles.editButton}
            icon={<EditOutlined />}
            disabled={!canEditTrip}
            onClick={() => navigate(`/trips/${trip.id}/edit`)}
          >
            {tripDetailEn.editTrip}
          </Button>
          <Button
            className={styles.hotelsButton}
            icon={<AccommodationIcon size={16} />}
            onClick={() => {
              if (canPlan) navigate(`/trips/${trip.id}/plan`);
              else message.info(tripDetailEn.needDatesForPlan);
            }}
          >
            {tripDetailEn.hotels}
          </Button>
        </div>
        <div className={styles.footerMeta}>
          <div className={styles.planningToggle}>
            <span>{tripDetailEn.planning}</span>
            <Switch checked={showPlanIcons} onChange={handleShowPlanIconsChange} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TripDetail;
